// ===== FILE: src/pages/Listing/ContractorStatePage.tsx ===== (UPDATED)
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { Link, useParams, useLoaderData } from 'react-router-dom';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import SEOTags from './SEOTags';
import ContractorListingGrid from './ContractorListingGrid';
import ListingPagination from './ListingPagination';
import { useContractors } from '../../hooks/useContractors';
import { useContractorLocations } from '../../hooks/useContractorLocations';
import { contractorAPI } from '../../services/contractorService';
import { Contractor } from '../../models/Contractor.interface';
import { buildCityUrl } from '../../utils/urlUtils';
import PopularCategoriesInLocation from './PopularCategoriesInLocation';
import Header from "../Home/Header";
import Footer from "../Home/Footer";

interface StatePageParams {
  state: string;
}

interface CityWithCount {
  name: string;
  count: number;
}

const ContractorStatePage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [stateDisplay, setStateDisplay] = useState('');
  const [cities, setCities] = useState<CityWithCount[]>([]);
  const [allContractors, setAllContractors] = useState<Contractor[]>([]);
  const [allContractorsLoading, setAllContractorsLoading] = useState(true);

  const { state } = useParams<{ state: string }>();
  const loaderData = useLoaderData() as StatePageParams;
  const itemsPerPage = 12;

  // Get contractors for this state (paginated for display)
  const {
    contractors,
    loading,
    error,
    pagination,
    setPage
  } = useContractors({
    state: loaderData.state,
    page: currentPage,
    limit: itemsPerPage
  }, true);

  // Get location data to extract cities in this state
  const { locations } = useContractorLocations();

  // Fetch ALL contractors for this state to get accurate category counts
  useEffect(() => {
    const fetchAllContractors = async () => {
      setAllContractorsLoading(true);
      try {
        console.log('🔍 Fetching all contractors for state:', loaderData.state);
        
        // Use the general contractors endpoint with just state filter
        const response = await contractorAPI.getContractors({
          state: loaderData.state,
          page: 1,
          limit: 10000
        });
        
        console.log('✅ Frontend: Received contractors for state:', {
          count: response.data.length,
          totalCount: response.pagination?.totalCount,
          state: loaderData.state
        });
        
        setAllContractors(response.data);
      } catch (err) {
        console.error('❌ Error fetching all contractors for category counts:', err);
        // Fallback to using the paginated contractors if the fetch fails
        setAllContractors(contractors);
      } finally {
        setAllContractorsLoading(false);
      }
    };

    if (loaderData.state) {
      fetchAllContractors();
    }
  }, [loaderData.state]);

  // Convert state slug back to display name
  useEffect(() => {
    if (loaderData.state) {
      const display = loaderData.state
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
      setStateDisplay(display);
    }
  }, [loaderData.state]);

  // Extract cities for this state from location data
  useEffect(() => {
    if (locations.length > 0 && loaderData.state) {
      const stateName = loaderData.state.replace(/-/g, ' ').toUpperCase();
      
      const stateCities = locations
        .filter(location => 
          location.city && 
          (location.state.toUpperCase() === stateName ||
           location.state.toLowerCase().replace(/\s+/g, '-') === loaderData.state)
        )
        .map(location => ({
          name: location.city!,
          count: location.count
        }))
        .sort((a, b) => b.count - a.count) // Sort by contractor count
        .slice(0, 20); // Limit to top 20 cities

      setCities(stateCities);
    }
  }, [locations, loaderData.state]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <React.Fragment>
      <Header isFullWidth={true} />
      <div className="page-content">
        <SEOTags
          title={`Contractors in ${stateDisplay}`}
          description={`Find qualified contractors in ${stateDisplay}. Browse our directory of professional contractors serving ${stateDisplay} for your project needs.`}
          schemaType="ItemList"
        />

        <Container fluid>
          <BreadCrumb title={`Contractors in ${stateDisplay}`} pageTitle="Contractors" />

          <Row>
            <Col lg={9}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    Contractors in {stateDisplay}
                    {!loading && (
                      <span className="text-muted ms-2">
                        ({pagination.totalCount} found)
                      </span>
                    )}
                  </h4>
                </CardHeader>

                <CardBody>
                  {error ? (
                    <div className="alert alert-danger">{error}</div>
                  ) : (
                    <>
                      {/* State Overview Stats */}
                      {!loading && pagination.totalCount > 0 && (
                        <div className="bg-light p-3 rounded mb-4">
                          <div className="row text-center">
                            <div className="col-md-4">
                              <h5 className="mb-1 text-primary">{pagination.totalCount}</h5>
                              <p className="text-muted mb-0">Total Contractors</p>
                            </div>
                            <div className="col-md-4">
                              <h5 className="mb-1 text-primary">{cities.length}</h5>
                              <p className="text-muted mb-0">Cities Served</p>
                            </div>
                            <div className="col-md-4">
                              <h5 className="mb-1 text-primary">
                                {contractors.length > 0 
                                  ? (contractors.reduce((sum, c) => sum + (c.rating || 0), 0) / contractors.length).toFixed(1)
                                  : '0.0'
                                }
                              </h5>
                              <p className="text-muted mb-0">Average Rating</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <ContractorListingGrid 
                        contractors={contractors} 
                        loading={loading}
                      />

                      {!loading && contractors.length === 0 && (
                        <div className="text-center py-5">
                          <h5 className="text-muted">No contractors found in {stateDisplay}</h5>
                          <p className="text-muted">
                            Try browsing our full contractor directory.
                          </p>
                          <Link 
                            to="/contractors"
                            className="btn btn-outline-primary"
                          >
                            View All Contractors
                          </Link>
                        </div>
                      )}

                      {pagination.totalPages > 1 && (
                        <ListingPagination
                          currentPage={pagination.currentPage}
                          totalPages={pagination.totalPages}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg={3}>
              {/* Popular Cities in State */}
              <Card className="mb-4">
                <CardHeader>
                  <h5 className="card-title mb-0">Popular Cities in {stateDisplay}</h5>
                </CardHeader>
                <CardBody>
                  {cities.length > 0 ? (
                    <div className="list-group list-group-flush">
                      {cities.map((city, index) => (
                        <Link
                          key={city.name}
                          to={buildCityUrl(loaderData.state, city.name)}
                          className="list-group-item list-group-item-action d-flex justify-content-between align-items-center border-0 px-0"
                        >
                          <div>
                            <span className="fw-medium">{city.name}</span>
                            {index < 3 && (
                              <span className="badge bg-primary ms-2">Popular</span>
                            )}
                          </div>
                          <span className="badge bg-light text-dark">
                            {city.count}
                          </span>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted mb-0">Loading cities...</p>
                  )}
                </CardBody>
              </Card>

              {/* Popular Categories in State - Using all contractors for accurate counts */}
              <PopularCategoriesInLocation 
                state={loaderData.state}
                contractors={allContractors} // Use all contractors instead of just current page
                title={`Popular Categories in ${stateDisplay}`}
                loading={allContractorsLoading}
              />
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default ContractorStatePage;